import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { socketUrl, type ChatMessage } from "../lib/api";
import { RoomE2EESession, formatSecurityCode, type E2EEStatus, isE2EEChatPayload } from "../lib/e2ee";

export type PeerInfo = {
  peerId: string;
  displayName: string;
  isHost: boolean;
};

async function normalizeMessage(session: RoomE2EESession | null, msg: ChatMessage): Promise<ChatMessage> {
  if (msg.type !== "TEXT" || !session) return msg;
  if (!isE2EEChatPayload(msg.content)) return msg;
  const content = await session.decryptChat(msg.content);
  if (isE2EEChatPayload(content)) {
    return { ...msg, content: "🔒 Encrypted message (waiting for room key…)" };
  }
  return { ...msg, content };
}

async function normalizeAll(session: RoomE2EESession | null, list: ChatMessage[]) {
  return Promise.all(list.map((m) => normalizeMessage(session, m)));
}

/** Socket.io chat, polls, and E2EE key exchange (media is LiveKit). */
export function useMeetingChat(options: {
  meetingId: string;
  token?: string | null;
  inviteToken?: string;
  guestName?: string;
  enabled: boolean;
}) {
  const [connected, setConnected] = useState(false);
  const [peerId, setPeerId] = useState<string | null>(null);
  const [peers, setPeers] = useState<PeerInfo[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [e2eeStatus, setE2eeStatus] = useState<E2EEStatus>({
    enabled: false,
    ready: false,
    securityCode: null,
    mediaLayer: "dtls-srtp",
    chatEncrypted: false,
    verified: false,
  });
  const [remoteFingerprints, setRemoteFingerprints] = useState<
    { peerId: string; fingerprint: string }[]
  >([]);
  const [e2eeVerified, setE2eeVerifiedState] = useState(false);
  const e2eeVerifiedRef = useRef(false);

  const socketRef = useRef<Socket | null>(null);
  const peerIdRef = useRef<string | null>(null);
  const e2eeRef = useRef<RoomE2EESession | null>(null);
  const hostPeerIdRef = useRef<string | null>(null);
  const peerJwkRef = useRef<Map<string, JsonWebKey>>(new Map());

  useEffect(() => {
    if (!options.enabled) return;
    let cancelled = false;

    void (async () => {
      const socket = io(socketUrl(), {
        transports: ["websocket"],
        auth: {
          token: options.token || undefined,
          meetingId: options.meetingId,
          inviteToken: options.inviteToken,
          guestName: options.guestName,
        },
      });
      if (cancelled) {
        socket.disconnect();
        return;
      }
      socketRef.current = socket;

      socket.on("connect", () => setConnected(true));
      socket.on("connect_error", (err) => setError(err.message));
      socket.on("disconnect", () => setConnected(false));

      socket.on(
        "room:joined",
        async (payload: {
          peerId: string;
          displayName: string;
          isHost: boolean;
          existingPeers: PeerInfo[];
          e2eeEnabled?: boolean;
          recordingEnabled?: boolean;
        }) => {
          try {
            setPeerId(payload.peerId);
            peerIdRef.current = payload.peerId;
            setPeers(payload.existingPeers);
            setRecordingEnabled(payload.recordingEnabled ?? true);

            if (payload.e2eeEnabled) {
              const session = new RoomE2EESession(payload.peerId);
              await session.enable();
              e2eeRef.current = session;
              const jwk = await session.exportPublicKeyJwk();
              peerJwkRef.current.set(payload.peerId, jwk);
              socket.emit("e2ee:public-key", { publicKeyJwk: jwk });

              if (payload.isHost) {
                hostPeerIdRef.current = payload.peerId;
                const raw = await session.generateMediaKeyAsHost();
                const fp = session.getFingerprint();
                if (fp) socket.emit("e2ee:fingerprint", { fingerprint: formatSecurityCode(fp) });
                for (const p of payload.existingPeers) {
                  const peerJwk = peerJwkRef.current.get(p.peerId);
                  if (peerJwk) {
                    const envelope = await session.wrapMediaKeyForPeer(peerJwk, raw);
                    socket.emit("e2ee:key-envelope", { to: p.peerId, ...envelope });
                  }
                }
              }
              setE2eeStatus(session.getStatus(e2eeVerifiedRef.current));
            } else {
              e2eeRef.current = null;
              setE2eeStatus({
                enabled: false,
                ready: true,
                securityCode: null,
                mediaLayer: "dtls-srtp",
                chatEncrypted: false,
                verified: true,
              });
            }
          } catch (e) {
            console.error("[chat] room:joined E2EE setup failed", e);
            setError(e instanceof Error ? e.message : "Encryption setup failed");
          }
        },
      );

      socket.on("peer:joined", (p: PeerInfo) => {
        setPeers((prev) => [...prev.filter((x) => x.peerId !== p.peerId), p]);
        if (p.isHost) hostPeerIdRef.current = p.peerId;
      });

      socket.on("peer:left", ({ peerId: leftId }: { peerId: string }) => {
        setPeers((prev) => prev.filter((p) => p.peerId !== leftId));
      });

      socket.on("chat:history", async (history: ChatMessage[]) => {
        try {
          setMessages(await normalizeAll(e2eeRef.current, history));
        } catch (e) {
          console.error("[chat] history decrypt failed", e);
          setMessages(history);
        }
      });

      socket.on("chat:message", async (msg: ChatMessage) => {
        try {
          const next = await normalizeMessage(e2eeRef.current, msg);
          setMessages((prev) => [...prev, next]);
        } catch (e) {
          console.error("[chat] message decrypt failed", e);
          setMessages((prev) => [...prev, msg]);
        }
      });

      socket.on(
        "chat:vote:updated",
        ({ messageId, responses }: { messageId: string; responses: ChatMessage["voteResponses"] }) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, voteResponses: responses } : m)),
          );
        },
      );

      socket.on(
        "e2ee:public-key",
        async (payload: { from: string; publicKeyJwk: JsonWebKey; isHost: boolean }) => {
          peerJwkRef.current.set(payload.from, payload.publicKeyJwk);
          if (payload.isHost) hostPeerIdRef.current = payload.from;
          const session = e2eeRef.current;
          if (!session?.isEnabled()) return;
          if (hostPeerIdRef.current === peerIdRef.current) {
            let raw = session.getMediaKeyRaw();
            if (!raw) {
              raw = await session.generateMediaKeyAsHost();
              const fp = session.getFingerprint();
              if (fp) socket.emit("e2ee:fingerprint", { fingerprint: formatSecurityCode(fp) });
            }
            const envelope = await session.wrapMediaKeyForPeer(payload.publicKeyJwk, raw);
            socket.emit("e2ee:key-envelope", { to: payload.from, ...envelope });
          }
          setE2eeStatus(session.getStatus(e2eeVerifiedRef.current));
        },
      );

      socket.on("e2ee:fingerprint", (payload: { from: string; fingerprint: string }) => {
        setRemoteFingerprints((prev) => [
          ...prev.filter((p) => p.peerId !== payload.from),
          { peerId: payload.from, fingerprint: payload.fingerprint },
        ]);
      });

      socket.on(
        "e2ee:key-envelope",
        async (payload: { from: string; iv: string; ciphertext: string }) => {
          try {
            const session = e2eeRef.current;
            const fromJwk = peerJwkRef.current.get(payload.from);
            if (!session || !fromJwk) return;
            await session.unwrapMediaKey(fromJwk, payload.iv, payload.ciphertext);
            const fp = session.getFingerprint();
            if (fp) socket.emit("e2ee:fingerprint", { fingerprint: formatSecurityCode(fp) });
            setE2eeStatus(session.getStatus(e2eeVerifiedRef.current));
          } catch (e) {
            console.error("[chat] key envelope failed", e);
          }
        },
      );
    })();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
    };
  }, [
    options.enabled,
    options.meetingId,
    options.token,
    options.inviteToken,
    options.guestName,
  ]);

  /** Chat goes over HTTPS + WSS; plaintext on server avoids key race bugs. Room key is for verification. */
  const sendChat = useCallback((content: string) => {
    socketRef.current?.emit("chat:send", { content });
  }, []);

  const createVote = useCallback((question: string, optionsList: string[]) => {
    socketRef.current?.emit("chat:vote:create", { question, options: optionsList });
  }, []);

  const castVote = useCallback((messageId: string, optionId: string) => {
    socketRef.current?.emit("chat:vote:cast", { messageId, optionId });
  }, []);

  useEffect(() => {
    const session = e2eeRef.current;
    if (!session?.hasMediaKey()) return;
    setMessages((prev) => {
      void normalizeAll(session, prev).then((next) => setMessages(next));
      return prev;
    });
  }, [e2eeStatus.ready, e2eeStatus.securityCode]);

  return {
    connected,
    peerId,
    peers,
    messages,
    error,
    recordingEnabled,
    e2eeStatus,
    remoteFingerprints,
    e2eeVerified,
    setE2eeVerified: (v: boolean) => {
      e2eeVerifiedRef.current = v;
      setE2eeVerifiedState(v);
      if (e2eeRef.current) setE2eeStatus(e2eeRef.current.getStatus(v));
    },
    sendChat,
    createVote,
    castVote,
    appendMessage: (msg: ChatMessage) => setMessages((prev) => [...prev, msg]),
  };
}
